import { useEffect, useMemo, useRef, useState } from "react";
import { generateClient, post } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import { uploadData } from "aws-amplify/storage";
import { useAuth } from "../AuthProvider";
import {
  createIDRCase,
  getDocumentExportApproval,
  getIDRAnalysisRun,
  listIDRAnalysisRunsByCase,
  listIDRCases,
  updateIDRCase,
} from "../graphql/idrCase";
import {
  createDocumentExportApprovalId,
  DOCUMENT_EXPORT_APPROVAL_PATH,
  hashExportContent,
  isDocumentExportApproved,
} from "../features/documentExportApproval";
import DocumentExportApprovalPanel from "./DocumentExportApprovalPanel";
import {
  DOCUMENT_ROLES,
  EVIDENCE_CLASSIFICATIONS,
  IDR_CASE_SCHEMA_VERSION,
  buildFactOnlyDraft,
  compareOfferWithBenchmark,
  compareOfferToQpa,
  createEmptyIdrCase,
  createReviewReport,
  getBlockingIssues,
  getIDRServiceLines,
  getPricingOfferIssues,
  parseMoneyToMinorUnits,
  parsePageAssignments,
  screenBenchmarkEligibility,
  screenComparatorEligibility,
  validateIdrCaseData,
  validateIdrManifest,
  validateSourceRef,
} from "../features/idr/idrWorkflow";
import "./IDRWorkflowScreen.css";

const IDR_API_NAME = process.env.REACT_APP_CODING_API_NAME || "accountRequestsAPI";
const IDR_ANALYSIS_PATH = process.env.REACT_APP_IDR_ANALYSIS_PATH || "/idr/analyze";
const IDR_CANCEL_PATH = process.env.REACT_APP_IDR_CANCEL_PATH || "/idr/cancel";
const IDR_DELETE_PATH = process.env.REACT_APP_IDR_DELETE_PATH || "/idr/delete";
const MAX_DOCUMENTS = 10;
const MAX_IMAGES = 6;
const MAX_TOTAL_BYTES = 24 * 1024 * 1024;
const SUPPORTED_EXTENSIONS = new Set([
  "pdf",
  "txt",
  "docx",
  "rtf",
  "md",
  "png",
  "jpg",
  "jpeg",
  "webp",
]);
const TABS = [
  ["case", "Case & sources"],
  ["clinical", "Medical necessity analysis"],
  ["pricing", "Pricing analysis"],
  ["legal", "Legal sources"],
  ["draft", "Draft"],
  ["review", "Evidence & review"],
];

function newId() {
  return window.crypto?.randomUUID?.() || `idr-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function cleanFileName(name) {
  return String(name || "document")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, "")
    .replace(/[\\/]+/g, "_")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 160);
}

function safeHttpUrl(value) {
  try {
    const url = new URL(value);
    return ["https:", "http:"].includes(url.protocol) ? url.toString() : "";
  } catch {
    return "";
  }
}

function getFileExtension(name) {
  return String(name || "").split(".").pop().toLowerCase();
}

function formatSourceRef(sourceRef, documents) {
  const document = documents.find((item) => item.id === sourceRef.documentId);
  const pageLabel = Number.isInteger(sourceRef.page) ? `, page ${sourceRef.page}` : ", page unavailable";
  return `${document?.name || sourceRef.documentId} (version ${sourceRef.documentVersion}${pageLabel})`;
}

function parseOptionalJson(value, label) {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch {
    throw new Error(`${label} is not valid JSON.`);
  }
}

function contentType(file) {
  if (file.type) return file.type;
  const extension = getFileExtension(file.name);
  if (extension === "pdf") return "application/pdf";
  if (["png", "jpg", "jpeg", "webp"].includes(extension)) {
    return extension === "jpg" ? "image/jpeg" : `image/${extension}`;
  }
  if (extension === "txt") return "text/plain";
  if (extension === "docx") {
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  if (extension === "rtf") return "application/rtf";
  if (extension === "md") return "text/markdown";
  return "application/octet-stream";
}

function toHex(buffer) {
  return Array.from(new Uint8Array(buffer), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

async function hashFile(file) {
  if (!window.crypto?.subtle) {
    throw new Error("This browser cannot create a source hash. Use a current browser over HTTPS.");
  }
  return toHex(await window.crypto.subtle.digest("SHA-256", await file.arrayBuffer()));
}

function compressPages(pages) {
  const sorted = [...new Set((pages || []).map(Number))].sort((a, b) => a - b);
  if (!sorted.length) return "";
  const ranges = [];
  let start = sorted[0];
  let end = sorted[0];
  for (const page of sorted.slice(1)) {
    if (page === end + 1) {
      end = page;
    } else {
      ranges.push(start === end ? `${start}` : `${start}-${end}`);
      start = page;
      end = page;
    }
  }
  ranges.push(start === end ? `${start}` : `${start}-${end}`);
  return ranges.join(",");
}

function requireGraphqlData(response, key) {
  if (response?.errors?.length) {
    throw new Error(response.errors.map((item) => item.message).filter(Boolean).join("; "));
  }
  if (!response?.data?.[key]) throw new Error(`The server returned no ${key} record.`);
  return response.data[key];
}

function editableFingerprint(idrCase, manifest) {
  const { draftMarkdown, draftInputFingerprint, draftReviewNotes, reviewHistory, ...inputs } = idrCase;
  return JSON.stringify({
    inputs,
    sources: manifest.map((document) => {
      const pageCount = String(document.pageCount || "").trim()
        ? Number(document.pageCount)
        : null;
      const pageAssignments = document.pageAssignmentsText
        ? parsePageAssignments(document.pageAssignmentsText, pageCount).assignments
        : document.pageAssignments || [];
      return {
        id: document.id,
        version: document.version,
        name: document.name,
        role: document.role,
        sizeBytes: document.sizeBytes,
        mimeType: document.mimeType,
        pageCount,
        pageAssignments,
        hash: document.hash,
        storageKey: document.storageKey,
      };
    }),
  });
}

function downloadMarkdown(markdown, filename) {
  const objectUrl = URL.createObjectURL(new Blob([markdown], { type: "text/markdown;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(objectUrl);
}

function InputField({ label, value, onChange, type = "text", placeholder, required = false }) {
  return (
    <label className="idr-field">
      <span>{label}{required ? " *" : ""}</span>
      <input
        type={type}
        value={value || ""}
        placeholder={placeholder}
        required={required}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function SourceUploadButton({ label, onChange }) {
  const inputRef = useRef(null);
  return (
    <div className="idr-file-picker">
      <input
        ref={inputRef}
        className="idr-upload-input"
        aria-label={label}
        type="file"
        multiple
        accept=".pdf,.txt,.docx,.rtf,.md,.png,.jpg,.jpeg,.webp"
        onChange={onChange}
        tabIndex={-1}
      />
      <button type="button" className="button" onClick={() => inputRef.current.click()}>
        {label}
      </button>
    </div>
  );
}

function TextField({ label, value, onChange, rows = 3, placeholder }) {
  return (
    <label className="idr-field idr-field-wide">
      <span>{label}</span>
      <textarea
        rows={rows}
        value={value || ""}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, options, emptyLabel = "Select..." }) {
  return (
    <label className="idr-field">
      <span>{label}</span>
      <select value={value || ""} onChange={(event) => onChange(event.target.value)}>
        <option value="">{emptyLabel}</option>
        {options.map(([optionValue, optionLabel]) => (
          <option key={optionValue} value={optionValue}>{optionLabel}</option>
        ))}
      </select>
    </label>
  );
}

function SourceReferenceFields({ label, sourceRef, documents, onChange }) {
  const sourceDocument = documents.find((document) => document.id === sourceRef?.documentId);
  return (
    <div className="idr-source-fields">
      <strong>{label}</strong>
      <SelectField
        label="Source document"
        value={sourceRef?.documentId || ""}
        onChange={(documentId) => {
          const document = documents.find((item) => item.id === documentId);
          onChange(document
            ? { documentId, documentVersion: document.version, page: sourceRef?.page ?? null }
            : null);
        }}
        options={documents.map((document) => [document.id, document.name])}
        emptyLabel="No source selected"
      />
      <InputField
        label="Physical page (optional)"
        value={sourceRef?.page ?? ""}
        onChange={(value) => onChange(sourceDocument
          ? {
              documentId: sourceDocument.id,
              documentVersion: sourceDocument.version,
              page: value ? Number(value) : null,
            }
          : null)}
        type="number"
      />
    </div>
  );
}

export default function IDRWorkflowScreen({ onExit }) {
  const { user } = useAuth();
  const client = useMemo(() => generateClient(), []);
  const [cases, setCases] = useState([]);
  const [caseId, setCaseId] = useState("");
  const [artifactVersion, setArtifactVersion] = useState(0);
  const [idrCase, setIdrCase] = useState(createEmptyIdrCase);
  const [manifest, setManifest] = useState([]);
  const [activeTab, setActiveTab] = useState("case");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisCancelLoading, setAnalysisCancelLoading] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState(null);
  const [analysisInputFingerprint, setAnalysisInputFingerprint] = useState("");
  const [caseApproval, setCaseApproval] = useState(null);
  const [approvalHashMatchesDraft, setApprovalHashMatchesDraft] = useState(false);
  const [currentDraftHash, setCurrentDraftHash] = useState("");
  const [approvalLookupLoading, setApprovalLookupLoading] = useState(false);
  const [approvalConfirmed, setApprovalConfirmed] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const analysisStatusRef = useRef(null);
  const [clinicalAnalysis, setClinicalAnalysis] = useState(null);
  const [pricingAnalysis, setPricingAnalysis] = useState(null);
  const [legalResearchAnalysis, setLegalResearchAnalysis] = useState(null);
  const [analysisRuns, setAnalysisRuns] = useState([]);
  const [analysisRunsNextToken, setAnalysisRunsNextToken] = useState(null);
  const [analysisRunDetails, setAnalysisRunDetails] = useState({});
  const [loadingAnalysisRunId, setLoadingAnalysisRunId] = useState("");
  const [deletingCase, setDeletingCase] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("");
  const [newDocumentRole, setNewDocumentRole] = useState(DOCUMENT_ROLES[0]);
  const [clinicalEntry, setClinicalEntry] = useState({
    finding: "",
    action: "",
    effect: "",
    classification: "UNCERTAIN",
    documentId: "",
    page: "",
  });
  const [comparatorEntry, setComparatorEntry] = useState({
    serviceLineId: "line_1",
    planName: "",
    administrator: "",
    physicianOrGroup: "",
    codeAndModifiers: "",
    serviceDates: "",
    location: "",
    serviceScope: "",
    amount: "",
    currency: "USD",
    basis: "",
    paymentBasis: "",
    comparability: "PENDING_REVIEW",
    comparabilityReasons: "",
    documentId: "",
    page: "",
  });
  const [legalEntry, setLegalEntry] = useState({
    title: "",
    formalCitation: "",
    url: "",
    locator: "",
    proposition: "",
    limitation: "",
  });

  const blockers = useMemo(() => getBlockingIssues(idrCase), [idrCase]);
  const currentFingerprint = useMemo(
    () => editableFingerprint(idrCase, manifest),
    [idrCase, manifest]
  );
  const pricingAnalysisIsCurrent = Boolean(
    Number(analysisStatus?.artifactVersion) === artifactVersion &&
    analysisInputFingerprint === currentFingerprint
  );
  const currentPricingAnalysis = pricingAnalysisIsCurrent ? pricingAnalysis : null;
  const pricingCodeDiscrepancies = currentPricingAnalysis?.codeDiscrepancies || [];
  const serviceLineIds = (idrCase.intakeMode === "free_text"
    ? currentPricingAnalysis?.extractedServiceLines || []
    : getIDRServiceLines(idrCase)).map((line) => line.lineId);
  const hasCurrentPricingAssessment = Boolean(
    pricingAnalysisIsCurrent &&
    analysisStatus?.pricing?.status === "COMPLETE_NEEDS_REVIEW" &&
    (idrCase.intakeMode !== "free_text" || serviceLineIds.length > 0) &&
    Array.isArray(currentPricingAnalysis?.serviceLineAssessments) &&
    currentPricingAnalysis.serviceLineAssessments.length === serviceLineIds.length &&
    serviceLineIds.every((lineId) =>
      currentPricingAnalysis.serviceLineAssessments.some((assessment) => assessment.lineId === lineId)
    )
  );
  const hasCurrentClinicalAssessment = Boolean(
    pricingAnalysisIsCurrent &&
    analysisStatus?.clinical?.status === "COMPLETE_NEEDS_REVIEW" &&
    clinicalAnalysis
  );
  const pricingOfferIssues = getPricingOfferIssues(idrCase);
  const hasPricingSources = manifest.some((document) => {
    const assignments = document.pageAssignmentsText
      ? parsePageAssignments(document.pageAssignmentsText, Number(document.pageCount) || null).assignments
      : document.pageAssignments;
    const roles = assignments?.length ? assignments.map((assignment) => assignment.role) : [document.role];
    return roles.some((role) =>
      ["TARGET_CLAIM_REMITTANCE", "COMPARATOR_EOB", "COMPARATOR_AWARD_AGREEMENT", "BENCHMARK", "PAYER_SUBMISSION"].includes(role)
    );
  });
  const hasTargetRemittance = manifest.some(
    (document) =>
      document.role === "TARGET_CLAIM_REMITTANCE" &&
      document.storageKey &&
      (!document.pageAssignments?.length ||
        document.pageAssignments.some((assignment) => assignment.role === "TARGET_CLAIM_REMITTANCE"))
  );
  const pricingOutputRequired = (idrCase.outputMode || "pricing_statement") !== "clinical_support_letter";
  const reviewReport = useMemo(
    () => createReviewReport(idrCase, manifest, analysisStatus),
    [idrCase, manifest, analysisStatus]
  );
  const isDraftStale = Boolean(
    idrCase.draftMarkdown &&
    idrCase.draftInputFingerprint &&
    idrCase.draftInputFingerprint !== currentFingerprint
  );
  const isApprovedForExport = Boolean(
    isDocumentExportApproved(caseApproval, {
      subjectType: "IDR_CASE",
      subjectId: caseId,
      artifactVersion,
      contentHash: currentDraftHash,
      analysisRunId: analysisStatus?.runId || null,
    }) &&
    approvalHashMatchesDraft &&
    !["QUEUED", "RUNNING"].includes(analysisStatus?.status) &&
    !isDraftStale &&
    blockers.length === 0 &&
    pricingCodeDiscrepancies.length === 0 &&
    (!pricingOutputRequired || hasCurrentPricingAssessment) &&
    (!hasTargetRemittance || hasCurrentPricingAssessment)
  );
  const firstServiceLine = getIDRServiceLines(idrCase)[0];
  const qpaComparison = useMemo(() => compareOfferToQpa(
    {
      amount: firstServiceLine?.offerAmount || "",
      currency: idrCase.offer.currency,
      basis: firstServiceLine?.offerBasis || "",
    },
    {
      amount: firstServiceLine?.qpaAmount || "",
      currency: idrCase.offer.currency,
      basis: firstServiceLine?.qpaBasis || "",
    }
  ), [firstServiceLine?.offerAmount, firstServiceLine?.offerBasis, firstServiceLine?.qpaAmount, firstServiceLine?.qpaBasis, idrCase.offer.currency]);
  const benchmarkComparison = useMemo(
    () => compareOfferWithBenchmark(
      {
        amount: firstServiceLine?.offerAmount || "",
        currency: idrCase.offer.currency,
        basis: firstServiceLine?.offerBasis || "",
      },
      idrCase.benchmark
    ),
    [firstServiceLine?.offerAmount, firstServiceLine?.offerBasis, idrCase.offer.currency, idrCase.benchmark]
  );
  const listedCases = useMemo(
    () => [...cases].sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0)),
    [cases]
  );

  const setCaseField = (section, key, value) => {
    setIdrCase((previous) => ({
      ...previous,
      [section]: { ...previous[section], [key]: value },
    }));
  };

  const loadCases = async () => {
    if (!user?.username) return;
    setLoading(true);
    setError("");
    try {
      const items = [];
      let nextToken = null;
      do {
        const response = await client.graphql({
          query: listIDRCases,
          variables: {
            filter: { userId: { eq: user.username } },
            limit: 100,
            nextToken,
          },
        });
        if (response?.errors?.length) {
          throw new Error(response.errors.map((item) => item.message).filter(Boolean).join("; "));
        }
        items.push(...(response?.data?.listIDRCases?.items || []).filter(Boolean));
        nextToken = response?.data?.listIDRCases?.nextToken || null;
      } while (nextToken);
      setCases(items);
      const selected = items.find((item) => item.id === caseId);
      if (selected) {
        const savedStatus = parseOptionalJson(selected.analysisStatusJson, "Analysis status");
        const currentRunId = analysisStatusRef.current?.runId;
        if (!currentRunId || savedStatus?.runId === currentRunId) {
          setAnalysisStatus(savedStatus);
          setClinicalAnalysis(parseOptionalJson(selected.clinicalAnalysisJson, "Clinical analysis"));
          setPricingAnalysis(parseOptionalJson(selected.pricingAnalysisJson, "Pricing analysis"));
          setLegalResearchAnalysis(parseOptionalJson(selected.legalResearchJson, "Legal research"));
        }
      }
    } catch (loadError) {
      setError(`Could not load IDR cases: ${loadError.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadAnalysisRuns = async (nextToken = null, append = false) => {
    if (!caseId) {
      setAnalysisRuns([]);
      setAnalysisRunsNextToken(null);
      return;
    }
    try {
      const response = await client.graphql({
        query: listIDRAnalysisRunsByCase,
        variables: {
          caseId,
          sortDirection: "DESC",
          limit: 25,
          nextToken,
        },
      });
      if (response?.errors?.length) {
        throw new Error(response.errors.map((item) => item.message).filter(Boolean).join("; "));
      }
      const result = response?.data?.listIDRAnalysisRunsByCase;
      setAnalysisRuns((previous) =>
        append ? [...previous, ...(result?.items || []).filter(Boolean)] : (result?.items || []).filter(Boolean)
      );
      setAnalysisRunsNextToken(result?.nextToken || null);
    } catch (historyError) {
      setError(`Could not load immutable analysis history: ${historyError.message}`);
    }
  };

  const loadAnalysisRunDetails = async (runId) => {
    if (analysisRunDetails[runId]) {
      setAnalysisRunDetails((previous) => {
        const next = { ...previous };
        delete next[runId];
        return next;
      });
      return;
    }
    setLoadingAnalysisRunId(runId);
    try {
      const response = await client.graphql({
        query: getIDRAnalysisRun,
        variables: { id: runId },
      });
      if (response?.errors?.length) {
        throw new Error(response.errors.map((item) => item.message).filter(Boolean).join("; "));
      }
      const result = response?.data?.getIDRAnalysisRun;
      if (!result || result.caseId !== caseId) {
        throw new Error("The archived run does not belong to the selected case.");
      }
      setAnalysisRunDetails((previous) => ({ ...previous, [runId]: result }));
    } catch (detailError) {
      setError(`Could not load archived analysis run: ${detailError.message}`);
    } finally {
      setLoadingAnalysisRunId("");
    }
  };

  useEffect(() => {
    analysisStatusRef.current = analysisStatus;
  }, [analysisStatus]);

  useEffect(() => {
    let active = true;
    setApprovalHashMatchesDraft(false);
    setCurrentDraftHash("");
    if (!caseId || !artifactVersion || !idrCase.draftMarkdown) {
      setCaseApproval(null);
      setApprovalLookupLoading(false);
      return () => { active = false; };
    }
    setApprovalLookupLoading(true);
    hashExportContent(idrCase.draftMarkdown)
      .then(async (contentHash) => {
        if (!active) return;
        setCurrentDraftHash(contentHash);
        const response = await client.graphql({
          query: getDocumentExportApproval,
          variables: {
            id: createDocumentExportApprovalId(
              "IDR_CASE",
              caseId,
              artifactVersion,
              contentHash,
              analysisStatus?.runId || null
            ),
          },
        });
        if (response?.errors?.length) {
          throw new Error(response.errors.map((item) => item.message).filter(Boolean).join("; "));
        }
        const approval = response?.data?.getDocumentExportApproval || null;
        if (approval && (
          approval.subjectType !== "IDR_CASE" ||
          approval.subjectId !== caseId ||
          Number(approval.artifactVersion) !== artifactVersion ||
          approval.contentHash !== contentHash
        )) {
          throw new Error("The approval does not match the selected draft.");
        }
        if (active) {
          setCaseApproval(approval);
          setApprovalHashMatchesDraft(approval?.contentHash === contentHash);
        }
      })
      .catch((hashError) => {
        if (active) {
          setCaseApproval(null);
          setError(`Could not verify approval against the current draft: ${hashError.message}`);
        }
      })
      .finally(() => {
        if (active) setApprovalLookupLoading(false);
      });
    return () => { active = false; };
  }, [caseId, artifactVersion, idrCase.draftMarkdown, analysisStatus?.runId, client]);

  useEffect(() => {
    loadCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.username]);

  useEffect(() => {
    setAnalysisRuns([]);
    setAnalysisRunsNextToken(null);
    setAnalysisRunDetails({});
    if (caseId) loadAnalysisRuns();
    // A new case selection loads its separately owner-authorized run history.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, user?.username]);

  useEffect(() => {
    if (!["QUEUED", "RUNNING"].includes(analysisStatus?.status)) return undefined;
    const timer = window.setInterval(() => {
      loadCases();
      loadAnalysisRuns();
    }, 4000);
    return () => window.clearInterval(timer);
    // Poll the selected owner-scoped case while its backend run is active.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisStatus?.status, caseId, user?.username]);

  const openCase = (record) => {
    try {
      const savedCase = JSON.parse(record.workflowJson);
      const parsedCase = {
        ...savedCase,
        intakeMode: "free_text",
        offerNarrative: savedCase.offerNarrative || "",
        outputMode: savedCase.outputMode || "pricing_statement",
        serviceLines: getIDRServiceLines(savedCase),
        payments: {
          ...createEmptyIdrCase().payments,
          ...(savedCase.payments || {}),
        },
        draftReviewNotes: savedCase.draftReviewNotes || [],
      };
      const parsedManifest = JSON.parse(record.manifestJson).map((document) => ({
        ...document,
        pageAssignmentsText: (document.pageAssignments || [])
          .map((assignment) => `${compressPages(assignment.pages)}=${assignment.role}`)
          .join("\n"),
      }));
      const validationErrors = [
        ...validateIdrCaseData(parsedCase),
        ...validateIdrManifest(parsedManifest),
      ];
      if (validationErrors.length) {
        throw new Error(validationErrors.join(" "));
      }
      setIdrCase(parsedCase);
      setManifest(parsedManifest);
      setCaseId(record.id);
      setArtifactVersion(Number(record.artifactVersion || 0));
      const savedAnalysisStatus = parseOptionalJson(record.analysisStatusJson, "Analysis status");
      setAnalysisStatus(savedAnalysisStatus);
      setAnalysisInputFingerprint(
        savedCase.intakeMode === "free_text" &&
        savedAnalysisStatus &&
        Number(savedAnalysisStatus.artifactVersion) === Number(record.artifactVersion)
          ? editableFingerprint(parsedCase, parsedManifest)
          : ""
      );
      setClinicalAnalysis(parseOptionalJson(record.clinicalAnalysisJson, "Clinical analysis"));
      setPricingAnalysis(parseOptionalJson(record.pricingAnalysisJson, "Pricing analysis"));
      setLegalResearchAnalysis(parseOptionalJson(record.legalResearchJson, "Legal research"));
      setCaseApproval(null);
      setApprovalConfirmed(false);
      setStatus(`Opened ${record.caseName || "IDR case"}.`);
      setError("");
    } catch (parseError) {
      setError(`Could not open case: ${parseError.message}`);
    }
  };

  const startNewCase = () => {
    setIdrCase(createEmptyIdrCase());
    setManifest([]);
    setCaseId("");
    setArtifactVersion(0);
    setAnalysisStatus(null);
    setAnalysisInputFingerprint("");
    setClinicalAnalysis(null);
    setPricingAnalysis(null);
    setLegalResearchAnalysis(null);
    setCaseApproval(null);
    setApprovalHashMatchesDraft(false);
    setCurrentDraftHash("");
    setApprovalLookupLoading(false);
    setApprovalConfirmed(false);
    setAnalysisRuns([]);
    setAnalysisRunsNextToken(null);
    setActiveTab("case");
    setStatus("New case. Enter known facts; unknown values remain blank.");
    setError("");
  };

  const addFiles = (event, assignedRole = newDocumentRole) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;
    const totalCount = manifest.length + files.length;
    const totalBytes = manifest.reduce((sum, document) => sum + Number(document.sizeBytes || 0), 0) +
      files.reduce((sum, file) => sum + file.size, 0);
    const imageCount = manifest.filter((document) => document.isImage).length +
      files.filter((file) => ["png", "jpg", "jpeg", "webp"].includes(getFileExtension(file.name))).length;
    const unsupported = files.filter((file) => !SUPPORTED_EXTENSIONS.has(getFileExtension(file.name)));
    if (unsupported.length) {
      setError(`Unsupported file type(s): ${unsupported.map((file) => file.name).join(", ")}.`);
      return;
    }
    if (totalCount > MAX_DOCUMENTS || imageCount > MAX_IMAGES || totalBytes > MAX_TOTAL_BYTES) {
      setError(`Upload limit exceeded: maximum ${MAX_DOCUMENTS} documents, ${MAX_IMAGES} images, and 24 MiB total.`);
      return;
    }
    setError("");
    setManifest((previous) => [
      ...previous,
      ...files.map((file) => ({
        id: newId(),
        version: 1,
        name: file.name,
        role: assignedRole,
        sizeBytes: file.size,
        mimeType: contentType(file),
        pageCount: "",
        pageAssignments: [],
        pageAssignmentsText: "",
        isImage: ["png", "jpg", "jpeg", "webp"].includes(getFileExtension(file.name)),
        hash: "",
        storageKey: "",
        extractionStatus: "NOT_RUN",
        pageStatus: "NOT_PROCESSED",
        pendingFile: file,
      })),
    ]);
  };

  const updateDocument = (documentId, patch) => {
    setManifest((previous) =>
      previous.map((document) => (document.id === documentId ? { ...document, ...patch } : document))
    );
  };

  const removeDocument = (documentId) => {
    setManifest((previous) => previous.filter((document) => document.id !== documentId));
  };

  const validateManifest = () => {
    for (const document of manifest) {
      const pageCountText = String(document.pageCount || "").trim();
      if (
        pageCountText &&
        (!Number.isInteger(Number(pageCountText)) || Number(pageCountText) < 1 || Number(pageCountText) > 10000)
      ) {
        return `${document.name}: enter a recorded page count from 1 to 10,000, or leave it unknown.`;
      }
      const parsed = parsePageAssignments(
        document.pageAssignmentsText || "",
        document.pageCount || null
      );
      if (parsed.errors.length) {
        return `${document.name}: ${parsed.errors.join(" ")}`;
      }
      if (!DOCUMENT_ROLES.includes(document.role)) {
        return `${document.name}: select a supported document role.`;
      }
    }
    return "";
  };

  const saveCase = async () => {
    if (!user?.username) {
      setError("You must be signed in to save an IDR case.");
      return;
    }
    const manifestError = validateManifest();
    if (manifestError) {
      setError(manifestError);
      setActiveTab("case");
      return;
    }
    const caseValidationErrors = validateIdrCaseData(idrCase);
    if (caseValidationErrors.length) {
      setError(`Case data is invalid: ${caseValidationErrors.join(" ")}`);
      return;
    }

    setSaving(true);
    setError("");
    setStatus("Uploading private evidence and saving the case...");
    const nextCaseId = caseId || `idr-${newId()}`;
    const priorCase = cases.find((item) => item.id === nextCaseId);
    const uploadedManifest = [];
    try {
      const authSession = await fetchAuthSession();
      const currentStorageIdentityId = String(authSession?.identityId || "").trim();
      if (manifest.length && !currentStorageIdentityId) {
        throw new Error("Private storage identity is unavailable. Sign in again before saving uploaded sources.");
      }
      if (
        priorCase?.storageIdentityId &&
        currentStorageIdentityId &&
        priorCase.storageIdentityId !== currentStorageIdentityId
      ) {
        throw new Error("The private storage identity changed. Reopen the case with its original account before saving.");
      }
      const storageIdentityId = priorCase?.storageIdentityId || currentStorageIdentityId || null;
      for (const document of manifest) {
        const { pendingFile, pageAssignmentsText, ...persisted } = document;
        const pageCount = String(document.pageCount || "").trim()
          ? Number(document.pageCount)
          : null;
        const pageAssignments = parsePageAssignments(
          pageAssignmentsText || "",
          pageCount
        ).assignments;
        let storageKey = persisted.storageKey;
        let hash = persisted.hash;
        if (pendingFile && !storageKey) {
          hash = await hashFile(pendingFile);
          storageKey = `${user.username}/idr-docs/${nextCaseId}/${document.id}/${cleanFileName(document.name)}`;
          const uploaded = await uploadData({
            key: storageKey,
            data: pendingFile,
            options: {
              accessLevel: "private",
              contentType: document.mimeType,
            },
          }).result;
          storageKey = uploaded?.key || storageKey;
        }
        uploadedManifest.push({
          ...persisted,
          pageCount,
          pageAssignments,
          hash,
          storageKey,
          uploadedAt: storageKey ? persisted.uploadedAt || new Date().toISOString() : null,
        });
      }
      const manifestValidationErrors = validateIdrManifest(uploadedManifest);
      if (manifestValidationErrors.length) {
        throw new Error(manifestValidationErrors.join(" "));
      }

      const previousWorkflow = priorCase?.workflowJson ? JSON.parse(priorCase.workflowJson) : null;
      const changedFields = [];
      if (previousWorkflow) {
        ["metadata", "offer", "offerNarrative", "intakeMode", "payments", "benchmark", "clinicalFindings", "comparators", "legalSources", "payerArguments"]
          .forEach((key) => {
            if (JSON.stringify(previousWorkflow[key]) !== JSON.stringify(idrCase[key])) {
              changedFields.push(key);
            }
          });
      }
      const caseToSave = {
        ...idrCase,
        schemaVersion: IDR_CASE_SCHEMA_VERSION,
        reviewHistory: changedFields.length
          ? [
              ...(idrCase.reviewHistory || []),
              {
                author: user.username,
                timestamp: new Date().toISOString(),
                source: "USER_EDIT",
                changedFields,
              },
            ]
          : idrCase.reviewHistory || [],
      };

      const sourceInputsChanged = !previousWorkflow || editableFingerprint(previousWorkflow, JSON.parse(priorCase.manifestJson)) !== editableFingerprint(caseToSave, uploadedManifest);
      const nextVersion = sourceInputsChanged ? artifactVersion + 1 : artifactVersion;
      const hasBlockingIssues = getBlockingIssues(caseToSave).length > 0;
      const hasCurrentDraft =
        Boolean(caseToSave.draftMarkdown) &&
        caseToSave.draftInputFingerprint === editableFingerprint(caseToSave, uploadedManifest);
      const staleAnalysisStatus = analysisStatus && sourceInputsChanged
        ? {
            ...analysisStatus,
            status: "STALE",
            updatedAt: new Date().toISOString(),
            clinical: analysisStatus.clinical
              ? { ...analysisStatus.clinical, status: "STALE" }
              : undefined,
            pricing: analysisStatus.pricing
              ? { ...analysisStatus.pricing, status: "STALE" }
              : undefined,
            legalResearch: analysisStatus.legalResearch
              ? { ...analysisStatus.legalResearch, status: "STALE" }
              : undefined,
          }
        : analysisStatus;
      const input = {
        id: nextCaseId,
        userId: user.username,
        caseName: String(
          caseToSave.metadata.disputeId ||
          caseToSave.metadata.targetIdentity ||
          uploadedManifest.find((document) => document.role === "TARGET_CLAIM_REMITTANCE")?.name ||
          uploadedManifest[0]?.name ||
          "Untitled IDR case"
        ).trim().slice(0, 120),
        storageIdentityId,
        processingStatus: hasBlockingIssues
          ? "NEEDS_INPUT"
          : !caseToSave.draftMarkdown
            ? "INTAKE_REVIEW"
            : hasCurrentDraft
              ? "DRAFT_REVIEW"
              : "DRAFT_STALE",
        workflowJson: JSON.stringify(caseToSave),
        manifestJson: JSON.stringify(uploadedManifest),
        draftMarkdown: caseToSave.draftMarkdown || null,
        artifactVersion: nextVersion,
        analysisRunStatus: staleAnalysisStatus?.status || null,
        analysisStatusJson: staleAnalysisStatus ? JSON.stringify(staleAnalysisStatus) : null,
      };
      const variables = { input };
      if (caseId) {
        variables.condition = {
          artifactVersion: { eq: artifactVersion },
          processingStatus: { ne: "DELETING" },
        };
      }
      const response = await client.graphql({
        query: caseId ? updateIDRCase : createIDRCase,
        variables,
      });
      const saved = requireGraphqlData(
        response,
        caseId ? "updateIDRCase" : "createIDRCase"
      );
      setCaseId(saved.id);
      setArtifactVersion(Number(saved.artifactVersion || nextVersion));
      setAnalysisStatus(staleAnalysisStatus);
      if (sourceInputsChanged) setAnalysisInputFingerprint("");
      setCaseApproval(null);
      setApprovalHashMatchesDraft(false);
      setApprovalConfirmed(false);
      setIdrCase(caseToSave);
      setManifest(uploadedManifest.map((document) => ({
        ...document,
        pageAssignmentsText: (document.pageAssignments || [])
          .map((assignment) => `${compressPages(assignment.pages)}=${assignment.role}`)
          .join("\n"),
      })));
      setStatus(`Saved artifact version ${saved.artifactVersion || nextVersion} (${input.processingStatus}). This is not an approved or filed offer.`);
      await loadCases();
    } catch (saveError) {
      setError(`Could not save IDR case: ${saveError.message}`);
      setStatus("");
    } finally {
      setSaving(false);
    }
  };

  const runIDRAnalysis = async () => {
    if (hasPricingSources && pricingOfferIssues.length) {
      setError(pricingOfferIssues.join(" "));
      setActiveTab("case");
      return;
    }
    if (!caseId || !artifactVersion) {
      setError("Save this case and its uploaded documents before starting analysis.");
      setActiveTab("case");
      return;
    }
    if (manifest.some((document) => document.pendingFile || !document.storageKey)) {
      setError("Save and upload every source document before starting analysis.");
      setActiveTab("case");
      return;
    }
    if (manifest.some((document) => getFileExtension(document.name) === "webp")) {
      setError("Convert WebP images to PNG or JPEG before starting OCR.");
      setActiveTab("case");
      return;
    }
    const selectedCase = cases.find((item) => item.id === caseId);
    if (!selectedCase) {
      setError("Refresh the saved cases before starting analysis.");
      setStatus("");
      return;
    }
    let savedWorkflow;
    let savedManifest;
    try {
      savedWorkflow = parseOptionalJson(selectedCase.workflowJson, "Saved case");
      savedManifest = JSON.parse(selectedCase.manifestJson);
    } catch (savedCaseError) {
      setError(`Could not verify saved case settings: ${savedCaseError.message}`);
      setStatus("");
      return;
    }
    const currentJurisdiction = String(idrCase.metadata?.jurisdiction || "")
      .trim()
      .toLocaleUpperCase("en-US");
    const savedJurisdiction = String(savedWorkflow?.metadata?.jurisdiction || "")
      .trim()
      .toLocaleUpperCase("en-US");
    if (
      savedWorkflow?.research?.enabled !== Boolean(idrCase.research?.enabled) ||
      (idrCase.research?.enabled && currentJurisdiction !== savedJurisdiction)
    ) {
      setError("Save the legal-research setting and jurisdiction before starting analysis.");
      setStatus("");
      return;
    }
    if (editableFingerprint(savedWorkflow, savedManifest) !== currentFingerprint) {
      setError("Save your IDR offer and uploaded source changes before starting analysis.");
      setActiveTab("case");
      return;
    }
    setAnalysisLoading(true);
    setError("");
    setStatus("Queueing OCR, separate clinical/pricing analyses, and enabled legal research...");
    try {
      const authSession = await fetchAuthSession();
      const accessToken = authSession?.tokens?.accessToken?.toString?.() || "";
      const idToken = authSession?.tokens?.idToken?.toString?.() || "";
      if (!accessToken || !idToken) throw new Error("Your sign-in session is unavailable. Sign in again.");
      const response = await post({
        apiName: IDR_API_NAME,
        path: IDR_ANALYSIS_PATH,
        options: {
          body: { caseId, artifactVersion, idToken },
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      }).response;
      const httpStatus = Number(response?.statusCode || response?.status || 200);
      const payload = await response.body.json();
      if (httpStatus >= 400 || payload?.ok === false) {
        throw new Error(payload?.error || `Analysis could not be started (${httpStatus}).`);
      }
      setAnalysisStatus(payload.analysisStatus || null);
      setAnalysisInputFingerprint(currentFingerprint);
      setStatus(
        payload.status === "QUEUED" || payload.status === "RUNNING"
          ? "OCR and analysis queued. Results require human review."
          : `Analysis status: ${payload.status || "unknown"}.`
      );
    } catch (analysisError) {
      setError(`Could not start IDR analysis: ${analysisError.message}`);
      setStatus("");
    } finally {
      setAnalysisLoading(false);
    }
  };

  const cancelIDRAnalysis = async () => {
    if (!caseId || !analysisStatus?.runId || analysisCancelLoading) return;
    const confirmed = window.confirm(
      "Cancel this IDR analysis? Any in-flight provider request may finish, but its result will not be saved."
    );
    if (!confirmed) return;
    setAnalysisCancelLoading(true);
    setError("");
    try {
      const authSession = await fetchAuthSession();
      const accessToken = authSession?.tokens?.accessToken?.toString?.() || "";
      if (!accessToken) throw new Error("Your sign-in session is unavailable. Sign in again.");
      const response = await post({
        apiName: IDR_API_NAME,
        path: IDR_CANCEL_PATH,
        options: {
          body: { caseId, runId: analysisStatus.runId },
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      }).response;
      const httpStatus = Number(response?.statusCode || response?.status || 200);
      const payload = await response.body.json();
      if (httpStatus >= 400 || payload?.ok !== true) {
        throw new Error(payload?.error || `Cancellation could not be saved (${httpStatus}).`);
      }
      const cancelledStatus = payload.analysisStatus || { ...analysisStatus, status: "CANCELLED" };
      setAnalysisStatus(cancelledStatus);
      setCases((previous) => previous.map((record) =>
        record.id === caseId
          ? {
              ...record,
              analysisRunStatus: "CANCELLED",
              analysisStatusJson: JSON.stringify(cancelledStatus),
            }
          : record
      ));
      setStatus(
        payload.message ||
        "Cancellation recorded. In-flight provider work may finish, but results will not be saved."
      );
      await loadAnalysisRuns();
    } catch (cancelError) {
      setError(`Could not cancel IDR analysis: ${cancelError.message}`);
      setStatus("");
    } finally {
      setAnalysisCancelLoading(false);
    }
  };

  const approveDraftForExport = async () => {
    if (!caseId || !artifactVersion || !idrCase.draftMarkdown || !approvalConfirmed) {
      setError("Save the current draft and confirm that you reviewed its sources before approving export.");
      return;
    }
    if (
      blockers.length ||
      pricingCodeDiscrepancies.length ||
      isDraftStale ||
      !manifest.length ||
      manifest.some((document) => !document.storageKey)
    ) {
      setError("Resolve blocking fields and remittance-code discrepancies, then save a current draft and all sources before approval.");
      return;
    }
    setApprovalLoading(true);
    setError("");
    try {
      const contentHash = await hashExportContent(idrCase.draftMarkdown);
      const authSession = await fetchAuthSession();
      const accessToken = authSession?.tokens?.accessToken?.toString?.() || "";
      const idToken = authSession?.tokens?.idToken?.toString?.() || "";
      if (!accessToken || !idToken) throw new Error("Your sign-in session is unavailable. Sign in again.");
      const response = await post({
        apiName: IDR_API_NAME,
        path: DOCUMENT_EXPORT_APPROVAL_PATH,
        options: {
          body: {
            subjectType: "IDR_CASE",
            subjectId: caseId,
            artifactVersion,
            contentHash,
            confirmReview: true,
            idToken,
          },
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      }).response;
      const httpStatus = Number(response?.statusCode || response?.status || 200);
      const payload = await response.body.json();
      if (httpStatus >= 400 || payload?.ok !== true || !payload.approval) {
        throw new Error(payload?.error || `Approval could not be saved (${httpStatus}).`);
      }
      setCaseApproval(payload.approval);
      setApprovalHashMatchesDraft(payload.approval.contentHash === contentHash);
      setApprovalConfirmed(false);
      setStatus("This saved draft is approved for export only. No filing or submission occurred.");
      await loadCases();
    } catch (approvalError) {
      setError(`Could not approve draft for export: ${approvalError.message}`);
      setStatus("");
    } finally {
      setApprovalLoading(false);
    }
  };

  const deleteCaseAndHistory = async () => {
    if (!caseId || deletingCase) return;
    const confirmed = window.confirm(
      "Delete this IDR case and all of its saved analysis history? This cannot be undone."
    );
    if (!confirmed) return;
    setDeletingCase(true);
    setError("");
    setStatus("Deleting the case and its run history...");
    try {
      const authSession = await fetchAuthSession();
      const accessToken = authSession?.tokens?.accessToken?.toString?.() || "";
      const idToken = authSession?.tokens?.idToken?.toString?.() || "";
      if (!accessToken || !idToken) throw new Error("Your sign-in session is unavailable. Sign in again.");
      const response = await post({
        apiName: IDR_API_NAME,
        path: IDR_DELETE_PATH,
        options: {
          body: { caseId, idToken },
          headers: { Authorization: `Bearer ${accessToken}` },
        },
      }).response;
      const httpStatus = Number(response?.statusCode || response?.status || 200);
      const payload = await response.body.json();
      if (httpStatus >= 400 || payload?.ok === false) {
        throw new Error(payload?.error || `Case deletion failed (${httpStatus}).`);
      }
      startNewCase();
      setStatus("IDR case, private uploads, analysis history, and approval history deleted.");
      await loadCases();
    } catch (deleteError) {
      setError(`Could not delete IDR case: ${deleteError.message}`);
      setStatus("");
    } finally {
      setDeletingCase(false);
    }
  };

  const generateDraft = () => {
    const manifestError = validateManifest();
    if (manifestError) {
      setError(manifestError);
      setActiveTab("case");
      return;
    }
    if (manifest.some((document) => document.pendingFile)) {
      setError("Save and upload attached documents before generating a source-linked draft.");
      setActiveTab("case");
      return;
    }
    if (
      (pricingOutputRequired && (!hasTargetRemittance || !hasCurrentPricingAssessment)) ||
      (hasTargetRemittance && !hasCurrentPricingAssessment)
    ) {
      setError(
        hasTargetRemittance
          ? "Run current pricing analysis on the Case EOB QPA before drafting so every line has an assessment and code discrepancies can be checked."
          : "Upload a Case EOB QPA and run current pricing analysis before creating a pricing statement or combined submission."
      );
      setActiveTab("pricing");
      return;
    }
    if (idrCase.outputMode === "combined_idr_submission" && !hasCurrentClinicalAssessment) {
      setError("Run current medical-note analysis before combining it with the pricing analysis.");
      setActiveTab("clinical");
      return;
    }
    const result = buildFactOnlyDraft(
      idrCase,
      manifest,
      idrCase.metadata.signatureDetails,
      analysisStatus?.pricing?.status === "COMPLETE_NEEDS_REVIEW"
        ? currentPricingAnalysis
        : null,
      hasCurrentClinicalAssessment ? clinicalAnalysis : null
    );
    if (result.status === "NEEDS_INPUT") {
      setError(`Draft blocked: ${result.blockers.map((item) => item.description).join(" ")}`);
      setActiveTab("case");
      return;
    }
    setIdrCase((previous) => ({
      ...previous,
      draftMarkdown: result.offerMarkdown,
      draftInputFingerprint: currentFingerprint,
      draftReviewNotes: result.reviewNotes || [],
    }));
    setError("");
    setStatus("Draft created. Review the separate internal review notes and verify every statement and cited source before use.");
  };

  const addClinicalFinding = () => {
    if (!clinicalEntry.finding.trim() || !clinicalEntry.action.trim() || !clinicalEntry.effect.trim()) {
      setError("Enter the documented finding, actual physician action, and recorded effect on work.");
      return;
    }
    const document = manifest.find((item) => item.id === clinicalEntry.documentId);
    const page = clinicalEntry.page ? Number(clinicalEntry.page) : null;
    const sourceRef = document
      ? { documentId: document.id, documentVersion: document.version, page }
      : null;
    if (sourceRef && !validateSourceRef(sourceRef, manifest).valid) {
      setError("Choose a valid document and page reference, or leave the source unselected for later review.");
      return;
    }
    setIdrCase((previous) => ({
      ...previous,
      clinicalFindings: [
        ...previous.clinicalFindings,
        {
          factId: newId(),
          finding: clinicalEntry.finding.trim(),
          action: clinicalEntry.action.trim(),
          effect: clinicalEntry.effect.trim(),
          classification: clinicalEntry.classification,
          evidenceKind: "USER_ATTESTED",
          sourceRef,
          reviewStatus: "NEEDS_HUMAN_REVIEW",
        },
      ],
    }));
    setClinicalEntry({
      finding: "",
      action: "",
      effect: "",
      classification: "UNCERTAIN",
      documentId: "",
      page: "",
    });
    setError("");
  };

  const addComparator = () => {
    if (!getIDRServiceLines(idrCase).some((line) => line.lineId === comparatorEntry.serviceLineId)) {
      setError("Select the exact disputed service line for this comparator.");
      return;
    }
    if (!comparatorEntry.amount.trim() || parseMoneyToMinorUnits(comparatorEntry.amount) === null) {
      setError("Enter a valid exact comparator amount; blank is unknown, not zero.");
      return;
    }
    const document = manifest.find((item) => item.id === comparatorEntry.documentId);
    const sourceRef = document
      ? {
          documentId: document.id,
          documentVersion: document.version,
          page: comparatorEntry.page ? Number(comparatorEntry.page) : null,
        }
      : null;
    if (sourceRef && !validateSourceRef(sourceRef, manifest).valid) {
      setError("The comparator source page is not valid for this document version.");
      return;
    }
    setIdrCase((previous) => ({
      ...previous,
      comparators: [
        ...previous.comparators,
        {
          comparatorId: newId(),
          ...comparatorEntry,
          permittedUse: "PENDING_REVIEW",
          reviewStatus: "NEEDS_HUMAN_REVIEW",
          sourceRef,
        },
      ],
    }));
    setComparatorEntry({
      serviceLineId: getIDRServiceLines(idrCase)[0]?.lineId || "",
      planName: "",
      administrator: "",
      physicianOrGroup: "",
      codeAndModifiers: "",
      serviceDates: "",
      location: "",
      serviceScope: "",
      amount: "",
      currency: "USD",
      basis: "",
      paymentBasis: "",
      comparability: "PENDING_REVIEW",
      comparabilityReasons: "",
      documentId: "",
      page: "",
    });
    setError("");
  };

  const addLegalSource = () => {
    if (!legalEntry.title.trim() && !legalEntry.formalCitation.trim()) {
      setError("Enter a title or formal citation for the source.");
      return;
    }
    setIdrCase((previous) => ({
      ...previous,
      legalSources: [
        ...previous.legalSources,
        {
          authorityId: newId(),
          ...legalEntry,
          sourceAccessStatus: "USER_SUPPLIED_NOT_VERIFIED",
          humanReviewStatus: "NOT_REVIEWED",
        },
      ],
    }));
    setLegalEntry({
      title: "",
      formalCitation: "",
      url: "",
      locator: "",
      proposition: "",
      limitation: "",
    });
    setError("");
  };

  return (
    <main className="container idr-workspace">
      <header className="idr-header">
        <div>
          <h1>IDR offer preparation</h1>
          <p>Prepare a source-linked, editable offer and separate review report. No dispute is filed or sent.</p>
        </div>
        <div className="idr-header-actions">
          {onExit && (
            <button type="button" className="button" onClick={onExit}>Back to LOMN</button>
          )}
          <button type="button" className="button" onClick={startNewCase}>New case</button>
          {caseId && (
            <button
              type="button"
              className="button"
              onClick={deleteCaseAndHistory}
              disabled={deletingCase || ["QUEUED", "RUNNING"].includes(analysisStatus?.status)}
            >
              {deletingCase ? "Deleting..." : "Delete case and history"}
            </button>
          )}
          <button type="button" className="button save" onClick={saveCase} disabled={saving}>
            {saving ? "Saving..." : "Save case"}
          </button>
        </div>
      </header>

      <aside className="idr-boundary-note">
        OCR and separate clinical/pricing model extraction can be queued for saved cases after the backend schema and model configuration are deployed. Legal web research and conservative comparator/benchmark screening are available after deployment; approval is limited to the saved IDR draft and local export. Filing is not implemented. Saved case activity is subject to seven-year retention cleanup after deployment.
      </aside>

      {error && <div role="alert" className="idr-alert idr-alert-error">{error}</div>}
      {status && <div role="status" className="idr-alert idr-alert-status">{status}</div>}

      <section className="idr-case-picker" aria-label="Saved IDR cases">
        <label>
          <span>Open a saved case</span>
          <select
            value={caseId}
            onChange={(event) => {
              const selected = cases.find((record) => record.id === event.target.value);
              if (selected) openCase(selected);
            }}
          >
            <option value="">Select saved case...</option>
            {listedCases.map((record) => (
              <option key={record.id} value={record.id}>
                {record.caseName} · {record.processingStatus} · v{record.artifactVersion}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="button" onClick={loadCases} disabled={loading}>
          {loading ? "Refreshing..." : "Refresh cases"}
        </button>
        {caseId && <span>Artifact version {artifactVersion}</span>}
      </section>

      <nav className="idr-tabs" aria-label="IDR workflow stages">
        {TABS.map(([tabId, label]) => (
          <button
            key={tabId}
            type="button"
            className={activeTab === tabId ? "is-active" : ""}
            aria-current={activeTab === tabId ? "step" : undefined}
            onClick={() => setActiveTab(tabId)}
          >
            {label}
            {tabId === "case" && blockers.length > 0 ? ` (${blockers.length})` : ""}
          </button>
        ))}
      </nav>

      <section className="idr-analysis-controls" aria-label="OCR and model analysis">
        <div>
          <strong>Analyze medical necessity and pricing separately</strong>
          <p>
            Uses only role-matched source documents. Results are source-linked extraction
            candidates for human review, not clinical determinations, comparator eligibility,
            legal research, or approval. Medical notes feed only the medical-necessity
            evidence analysis; Case EOB QPA and Example Payments feed only pricing.
            Describe your IDR offer before analyzing EOB data. Line items and QPA are extracted from the case EOB.
          </p>
          {analysisStatus && (
            <small>
              Run {analysisStatus.status || "UNKNOWN"}; clinical{" "}
              {analysisStatus.clinical?.status || "—"}
              {analysisStatus.clinical?.errorCode ? ` (${analysisStatus.clinical.errorCode})` : ""}
              ; pricing {analysisStatus.pricing?.status || "—"}
              {analysisStatus.pricing?.errorCode ? ` (${analysisStatus.pricing.errorCode})` : ""}.
              {idrCase.research?.enabled
                ? ` Legal research ${analysisStatus.legalResearch?.status || "NOT_RUN"}${analysisStatus.legalResearch?.errorCode ? ` (${analysisStatus.legalResearch.errorCode})` : ""}.`
                : " Legal research is disabled."}
              {Number(analysisStatus.artifactVersion) !== artifactVersion
                ? " These results are stale for the current artifact version."
                : ""}
            </small>
          )}
          <label className="idr-inline-control">
            <input
              type="checkbox"
              checked={Boolean(idrCase.research?.enabled)}
              onChange={(event) => setCaseField("research", "enabled", event.target.checked)}
            />
            Enable isolated federal legal research when I start analysis
          </label>
          <small>
            Save this setting before starting analysis. Only fixed legal questions and an optional
            state jurisdiction are sent; no patient identity, service date, claim amount, or
            document content is included. Starting analysis is the explicit search request.
          </small>
        </div>
        <button
          type="button"
          className="button"
          onClick={runIDRAnalysis}
          disabled={
            !caseId ||
            saving ||
            (hasPricingSources && pricingOfferIssues.length > 0) ||
            analysisLoading ||
            ["QUEUED", "RUNNING"].includes(analysisStatus?.status) ||
            (Number(analysisStatus?.artifactVersion) === artifactVersion &&
              analysisStatus?.status === "COMPLETED")
          }
        >
          {analysisLoading || ["QUEUED", "RUNNING"].includes(analysisStatus?.status)
            ? "Analysis in progress..."
            : Number(analysisStatus?.artifactVersion) === artifactVersion &&
              analysisStatus?.status === "COMPLETED"
              ? "Analysis complete — review results"
              : analysisStatus?.status === "PARTIAL"
                ? "Retry partial analysis"
              : "Run OCR and analysis"}
        </button>
        {["QUEUED", "RUNNING"].includes(analysisStatus?.status) && (
          <button
            type="button"
            className="button"
            onClick={cancelIDRAnalysis}
            disabled={!analysisStatus?.runId || analysisCancelLoading}
          >
            {analysisCancelLoading ? "Cancelling..." : "Cancel analysis"}
          </button>
        )}
        {analysisStatus?.status === "CANCELLED" && (
          <small>
            Analysis cancelled. An already-running provider request may have completed, but its
            result was not saved.
          </small>
        )}
      </section>

      {caseId && (
        <section className="idr-panel idr-analysis-history" aria-label="Immutable analysis history">
          <h2>Version-bound analysis history</h2>
          <p>Each run keeps its original case inputs and outputs. Editing creates a new artifact version; deleting the case removes its run history.</p>
          {analysisRuns.length === 0 ? (
            <p className="idr-muted">No saved analysis runs.</p>
          ) : (
            <ol>
              {analysisRuns.map((run) => {
                let savedStatus = { status: run.status };
                try {
                  savedStatus = JSON.parse(run.statusJson || "{}");
                } catch {
                  savedStatus = { status: run.status, statusJsonInvalid: true };
                }
                return (
                  <li key={run.id}>
                    <strong>Version {run.artifactVersion} · {savedStatus.status || run.status}</strong>
                    <span> · {new Date(run.createdAt).toLocaleString()} · run {run.runId}</span>
                    <small>Input SHA-256: <code>{run.inputHash}</code></small>
                      <button
                        type="button"
                        className="button"
                        onClick={() => loadAnalysisRunDetails(run.id)}
                        disabled={loadingAnalysisRunId === run.id}
                      >
                        {loadingAnalysisRunId === run.id
                          ? "Loading archived run..."
                          : analysisRunDetails[run.id]
                            ? "Hide archived run"
                            : "View archived inputs and outputs"}
                      </button>
                      {analysisRunDetails[run.id] && (
                        <pre>{JSON.stringify({
                          schemaVersion: analysisRunDetails[run.id].schemaVersion,
                          status: analysisRunDetails[run.id].statusJson,
                          workflowSnapshotJson: analysisRunDetails[run.id].workflowSnapshotJson,
                          manifestSnapshotJson: analysisRunDetails[run.id].manifestSnapshotJson,
                          clinicalAnalysisJson: analysisRunDetails[run.id].clinicalAnalysisJson,
                          pricingAnalysisJson: analysisRunDetails[run.id].pricingAnalysisJson,
                          legalResearchJson: analysisRunDetails[run.id].legalResearchJson,
                        }, null, 2)}</pre>
                      )}
                  </li>
                );
              })}
            </ol>
          )}
          {analysisRunsNextToken && (
            <button
              type="button"
              className="button"
              onClick={() => loadAnalysisRuns(analysisRunsNextToken, true)}
            >
              Load older runs
            </button>
          )}
        </section>
      )}

      {activeTab === "case" && (
        <section className="idr-panel">
          <h2>Uploads and IDR offer</h2>

          <section className="idr-subsection">
            <h3>Medical notes for medical-necessity analysis</h3>
            <p>Upload the target patient's medical notes here. They are not sent to the pricing model.</p>
            <SourceUploadButton label="Upload Medical Notes" onChange={(event) => addFiles(event, "TARGET_CLINICAL_RECORD")} />
            <p>{manifest.filter((document) => document.role === "TARGET_CLINICAL_RECORD").map((document) => document.name).join(", ") || "No medical notes attached."}</p>
          </section>

          <section className="idr-subsection" aria-label="EOB pricing uploads">
            <h3>EOB data for pricing analysis</h3>
            <p>Upload the disputed case EOB with its QPA separately from example payments. Line items and their QPA will be extracted from the case EOB and shown in pricing results for review.</p>
            <TextField
              label="Your IDR offer (required before pricing analysis)"
              value={idrCase.offerNarrative}
              rows={5}
              placeholder="Describe your offer in your own words, including the amount and services it covers."
              onChange={(value) => setIdrCase((previous) => ({
                ...previous, intakeMode: "free_text", offerNarrative: value,
              }))}
            />
            {pricingOfferIssues.length > 0 && (
              <ul className="idr-issue-list">{pricingOfferIssues.map((issue) => <li key={issue}>{issue}</li>)}</ul>
            )}
            <div className="idr-upload-controls">
              <SourceUploadButton label="Case EOB QPA" onChange={(event) => addFiles(event, "TARGET_CLAIM_REMITTANCE")} />
              <SourceUploadButton label="Example Payments" onChange={(event) => addFiles(event, "COMPARATOR_EOB")} />
            </div>
            <p>Case EOB QPA: {manifest.filter((document) => document.role === "TARGET_CLAIM_REMITTANCE").map((document) => document.name).join(", ") || "No case EOB attached."}</p>
            <p>Example Payments: {manifest.filter((document) => document.role === "COMPARATOR_EOB").map((document) => document.name).join(", ") || "No example payments attached."}</p>
          </section>

          <section className="idr-subsection">
            <h3>All source documents and page assignments</h3>
            <div className="idr-upload-controls">
              <SelectField
                label="Role for newly selected documents"
                value={newDocumentRole}
                onChange={setNewDocumentRole}
                options={DOCUMENT_ROLES.map((role) => [role, role.replaceAll("_", " ")])}
              />
              <label className="idr-file-picker">
                Add PDF, document, or supported image
                <input
                  aria-label="Add IDR source documents"
                  type="file"
                  multiple
                  accept=".pdf,.txt,.docx,.rtf,.md,.png,.jpg,.jpeg,.webp"
                  onChange={addFiles}
                />
              </label>
              <span>Maximum {MAX_DOCUMENTS} documents, {MAX_IMAGES} images, 24 MiB total.</span>
            </div>
            {!manifest.length && <p>No documents attached.</p>}
            <div className="idr-document-list">
              {manifest.map((document) => (
                <article className="idr-document-card" key={document.id}>
                  <div className="idr-document-heading">
                    <strong>{document.name}</strong>
                    <button
                      type="button"
                      className="button danger"
                      onClick={() => removeDocument(document.id)}
                      disabled={Boolean(document.storageKey)}
                      title={document.storageKey ? "Uploaded source retention/deletion is managed outside this workspace." : ""}
                    >
                      {document.storageKey ? "Retained" : "Remove"}
                    </button>
                  </div>
                  <div className="idr-grid">
                    <SelectField
                      label="Document role"
                      value={document.role}
                      onChange={(value) => updateDocument(document.id, { role: value })}
                      options={DOCUMENT_ROLES.map((role) => [role, role.replaceAll("_", " ")])}
                    />
                    <InputField
                      label="Page count (recorded)"
                      value={document.pageCount}
                      onChange={(value) => updateDocument(document.id, { pageCount: value })}
                      type="number"
                      placeholder="Leave blank if unknown"
                    />
                    <TextField
                      label="Mixed-PDF page roles (one range per line; overrides document role)"
                      value={document.pageAssignmentsText}
                      onChange={(value) => updateDocument(document.id, { pageAssignmentsText: value })}
                      rows={2}
                      placeholder={"1-3=TARGET_CLINICAL_RECORD\n4-6=COMPARATOR_EOB"}
                    />
                  </div>
                  <small>
                    {document.storageKey ? "Private upload saved" : "Not uploaded yet"} · {document.extractionStatus} · {document.pageStatus}
                    {document.hash ? ` · SHA-256 ${document.hash.slice(0, 16)}…` : ""}
                  </small>
                </article>
              ))}
            </div>
          </section>
        </section>
      )}

      {activeTab === "clinical" && (
        <section className="idr-panel">
          <h2>Medical necessity analysis</h2>
          <p>Model-extracted findings are evidence candidates only. Verify source pages and every interpretation before use.</p>
          {clinicalAnalysis && (
            <section className="idr-subsection" aria-label="Clinical model results">
              <h3>Model analysis — human review required</h3>
              {!pricingAnalysisIsCurrent && (
                <div className="idr-alert idr-alert-warning">
                  These medical results do not match the current offer and sources. Save and rerun analysis before drafting.
                </div>
              )}
              <p>{clinicalAnalysis.summary}</p>
              {clinicalAnalysis.extractionWarnings?.length > 0 && (
                <ul>
                  {clinicalAnalysis.extractionWarnings.map((warning, index) => (
                    <li key={`${warning.documentId}-${index}`}>
                      {manifest.find((item) => item.id === warning.documentId)?.name || warning.documentId}: {warning.warning}
                    </li>
                  ))}
                </ul>
              )}
              <div className="idr-record-list">
                {(clinicalAnalysis.findings || []).map((finding, index) => (
                  <article key={`${finding.finding}-${index}`}>
                    <strong>{finding.finding}</strong>
                    <p>{finding.relevance}</p>
                    <small>{finding.evidenceType}; verify against source</small>
                    {finding.sourceRefs?.length > 0 && (
                      <ul>
                        {finding.sourceRefs.map((sourceRef, refIndex) => (
                          <li key={`${sourceRef.documentId}-${sourceRef.page}-${refIndex}`}>
                            {formatSourceRef(sourceRef, manifest)}
                          </li>
                        ))}
                      </ul>
                    )}
                  </article>
                ))}
              </div>
              {clinicalAnalysis.missingInformation?.length > 0 && (
                <p><strong>Missing information:</strong> {clinicalAnalysis.missingInformation.join(" ")}</p>
              )}
              {clinicalAnalysis.riskFlags?.length > 0 && (
                <p><strong>Review flags:</strong> {clinicalAnalysis.riskFlags.join(" ")}</p>
              )}
            </section>
          )}
          <div className="idr-grid">
            <TextField label="Documented finding" value={clinicalEntry.finding} onChange={(value) => setClinicalEntry((previous) => ({ ...previous, finding: value }))} />
            <TextField label="Actual physician action" value={clinicalEntry.action} onChange={(value) => setClinicalEntry((previous) => ({ ...previous, action: value }))} />
            <TextField label="Documented effect on work" value={clinicalEntry.effect} onChange={(value) => setClinicalEntry((previous) => ({ ...previous, effect: value }))} />
            <SelectField label="Work classification" value={clinicalEntry.classification} onChange={(value) => setClinicalEntry((previous) => ({ ...previous, classification: value }))} options={EVIDENCE_CLASSIFICATIONS.map((item) => [item, item])} />
            <SelectField label="Source document" value={clinicalEntry.documentId} onChange={(value) => setClinicalEntry((previous) => ({ ...previous, documentId: value }))} options={manifest.map((item) => [item.id, item.name])} emptyLabel="No source selected" />
            <InputField label="Physical page (optional)" value={clinicalEntry.page} onChange={(value) => setClinicalEntry((previous) => ({ ...previous, page: value }))} type="number" />
          </div>
          <button type="button" className="button" onClick={addClinicalFinding}>Add documented work event</button>
          <div className="idr-record-list">
            {idrCase.clinicalFindings.map((finding) => (
              <article key={finding.factId}>
                <strong>{finding.finding}</strong>
                <p>{finding.action} — {finding.effect}</p>
                <small>{finding.classification}; {finding.evidenceKind}; requires human verification</small>
              </article>
            ))}
            {!idrCase.clinicalFindings.length && <p>No work events recorded.</p>}
          </div>
        </section>
      )}

      {activeTab === "pricing" && (
        <section className="idr-panel">
          <h2>Pricing analysis</h2>
          <p>Model-extracted payment and comparator candidates are not eligibility decisions or legal conclusions. Verify every source and payment basis. Code comparison is a structural remittance check, not authoritative coding advice.</p>
          {!pricingAnalysis && (
            <div className="idr-alert idr-alert-warning">
              No pricing analysis is available yet. The entered service codes have not been compared with the current remittance.
            </div>
          )}
          {pricingAnalysis && (
            <section className="idr-subsection" aria-label="Pricing model results">
              <h3>Model analysis — human review required</h3>
              {!pricingAnalysisIsCurrent && (
                <p className="idr-alert idr-alert-warning">These results are stale. Save your offer and sources, then rerun analysis.</p>
              )}
              <section aria-label="Extracted case QPA line items">
                <h3>Line items and QPA from the Case EOB QPA</h3>
                {(pricingAnalysis.extractedServiceLines || []).map((line) => (
                  <article key={line.lineId} className="idr-analysis-result">
                    <strong>{line.cptHcpcs}{line.modifiers ? ` / ${line.modifiers}` : ""}</strong>
                    <p>{line.description}{line.units ? `; units: ${line.units}` : ""}</p>
                    <p>QPA: {line.qpaAmount === null ? "Not found in the case EOB" : `${line.qpaAmount} ${line.currency || "(currency not stated)"}`}; basis: {line.qpaBasis || "not stated"}</p>
                    <small>{(line.sourceRefs || []).map((ref) => formatSourceRef(ref, manifest)).join("; ")}</small>
                  </article>
                ))}
                {!pricingAnalysis.extractedServiceLines?.length && (
                  <p>No case line items were extracted. Review the case EOB and missing-information warnings; QPA is never inferred from example payments.</p>
                )}
              </section>
              {pricingAnalysis.extractionWarnings?.length > 0 && (
                <ul>
                  {pricingAnalysis.extractionWarnings.map((warning, index) => (
                    <li key={`${warning.documentId}-${index}`}>
                      {manifest.find((item) => item.id === warning.documentId)?.name || warning.documentId}: {warning.warning}
                    </li>
                  ))}
                </ul>
              )}
              {(pricingAnalysis.paymentFacts || []).map((fact, index) => (
                <article key={`payment-${index}`} className="idr-analysis-result">
                  <strong>{fact.description}</strong>
                  <p>{fact.amount || "Amount unknown"} {fact.currency || ""}; type: {fact.paymentType || "not classified"}; {fact.lineId ? `service line ${fact.lineId}, code ${fact.cptHcpcs || "unknown"}` : "Case-level or unassigned payment figure — reference only; no service-line support is implied"}; basis: {fact.basis || "unknown"}</p>
                  <small>{(fact.sourceRefs || []).map((ref) => formatSourceRef(ref, manifest)).join("; ") || "No source reference returned"}</small>
                </article>
              ))}
              {(pricingAnalysis.codeDiscrepancies || []).map((item, index) => (
                <article key={`code-discrepancy-${item.lineId}-${index}`} className="idr-alert idr-alert-warning">
                  <strong>Code discrepancy: entered {item.enteredCode}; remittance shows {item.remittanceCode}</strong>
                  <p>{item.details}</p>
                  <small>{(item.sourceRefs || []).map((ref) => formatSourceRef(ref, manifest)).join("; ") || "No source reference returned"} · Resolve before drafting; automated comparison is not authoritative coding advice.</small>
                </article>
              ))}
              {(pricingAnalysis.serviceLineAssessments || []).map((assessment) => (
                <article key={`line-assessment-${assessment.lineId}`} className="idr-analysis-result">
                  <strong>{assessment.cptHcpcs}: {assessment.supportLevel?.replaceAll("_", " ") || "review required"}</strong>
                  <p>{assessment.amountSupportExplanation}</p>
                  {assessment.qpaComparisonExplanation && <p>QPA context only: {assessment.qpaComparisonExplanation}</p>}
                  <p>Supporting comparator IDs: {assessment.supportingComparatorIds.join(", ") || "None"}</p>
                  <p>Missing evidence: {assessment.missingEvidence.join("; ") || "None identified"}</p>
                  <small>{(assessment.sourceRefs || []).map((ref) => formatSourceRef(ref, manifest)).join("; ") || "No pricing source reference returned"}</small>
                </article>
              ))}
              {(pricingAnalysis.comparatorCandidates || []).map((candidate, index) => (
                <article key={`comparator-${index}`} className="idr-analysis-result">
                  <strong>Comparator candidate: {candidate.service || "Service unknown"}</strong>
                  <p>{candidate.amount || "Amount unknown"} {candidate.currency || ""}; {candidate.paymentBasis || "payment basis unknown"}; service date {candidate.serviceDate || "unknown"}</p>
                  <p>Limitations: {(candidate.limitations || []).join("; ") || "None extracted"}</p>
                  <small>{(candidate.sourceRefs || []).map((ref) => formatSourceRef(ref, manifest)).join("; ") || "No source reference returned"}</small>
                </article>
              ))}
              {(pricingAnalysis.benchmarkFacts || []).map((fact, index) => (
                <article key={`benchmark-${index}`} className="idr-analysis-result">
                  <strong>{fact.label}</strong>
                  <p>{fact.value}</p>
                  <p>Limitations: {(fact.limitations || []).join("; ") || "None extracted"}</p>
                  <small>{(fact.sourceRefs || []).map((ref) => formatSourceRef(ref, manifest)).join("; ") || "No source reference returned"}</small>
                </article>
              ))}
              {pricingAnalysis.missingInformation?.length > 0 && (
                <p><strong>Missing information:</strong> {pricingAnalysis.missingInformation.join(" ")}</p>
              )}
            </section>
          )}
          <section className="idr-subsection" aria-label="Benchmark completeness screen">
            <h3>Benchmark completeness screen</h3>
            {(() => {
              const screening = screenBenchmarkEligibility(
                idrCase.benchmark,
                idrCase.offer,
                manifest
              );
              return (
                <>
                  <p>
                    <strong>{screening.status}</strong> · permitted use: {screening.permittedUse}.
                    {" "}Valuation eligibility determined: no.
                  </p>
                  <ul>{screening.checks.map((check) => <li key={check.code}>{check.detail}</li>)}</ul>
                </>
              );
            })()}
          </section>
          {idrCase.intakeMode !== "free_text" && <div className="idr-calculation" role="status">
            <strong>Case-level payment reconciliation (reference only):</strong>{" "}
            {reviewReport.paymentReconciliation.status === "MATCH"
              ? `${reviewReport.paymentReconciliation.calculated} matches the recorded allowance reference figure.`
              : reviewReport.paymentReconciliation.status === "INCOMPLETE"
                ? "Incomplete; unknown amounts are not treated as zero."
                : reviewReport.paymentReconciliation.status === "MISMATCH"
                  ? `${reviewReport.paymentReconciliation.calculated} does not match the recorded allowance reference ${reviewReport.paymentReconciliation.allowance}.`
                  : reviewReport.paymentReconciliation.status}
          </div>}
          {idrCase.intakeMode !== "free_text" && <div className="idr-calculation" role="status">
            <strong>Service line 1 offer / QPA reference calculation:</strong>{" "}
            {qpaComparison
              ? `${qpaComparison.roundedMultiple} (${qpaComparison.method}; matching currency and explicit basis).`
              : "Not calculated: service line 1 needs a valid QPA and matching currency and amount basis. This is not a case-wide valuation."}
          </div>}
          <section className="idr-subsection">
            <h3>Optional benchmark reference (service line 1 only)</h3>
            <p>This is context only, not same-code allowance support and not evidence for any other line. User-entered benchmarks remain UNVERIFIED_CONTEXT_ONLY and AUDIT_ONLY.</p>
            <div className="idr-grid">
              <InputField
                label="Benchmark amount"
                value={idrCase.benchmark.amount}
                onChange={(value) => setCaseField("benchmark", "amount", value)}
                placeholder="e.g. 111000.00"
              />
              <InputField
                label="Benchmark currency"
                value={idrCase.benchmark.currency}
                onChange={(value) => setCaseField("benchmark", "currency", value)}
              />
              <InputField
                label="Benchmark amount basis"
                value={idrCase.benchmark.basis}
                onChange={(value) => setCaseField("benchmark", "basis", value)}
              />
              <TextField
                label="Source / methodology / population / limitations"
                value={idrCase.benchmark.provenance}
                onChange={(value) => setCaseField("benchmark", "provenance", value)}
              />
            </div>
            <SourceReferenceFields
              label="Benchmark source"
              sourceRef={idrCase.benchmark.sourceRef}
              documents={manifest}
              onChange={(sourceRef) => setCaseField("benchmark", "sourceRef", sourceRef)}
            />
            <div className="idr-calculation" role="status">
              <strong>Service line 1 benchmark reference difference:</strong>{" "}
              {benchmarkComparison
                ? `${benchmarkComparison.roundedPercent} using ${benchmarkComparison.method}. ${benchmarkComparison.interpretation}`
                : "Not calculated: matching currency and explicit amount basis are required."}
              <br />Use remains AUDIT_ONLY pending provenance, comparability, and human/legal review.
            </div>
          </section>
          <section className="idr-subsection">
            <h3>Comparator payment</h3>
            <div className="idr-grid">
              <SelectField
                label="Target service line"
                value={comparatorEntry.serviceLineId}
                onChange={(value) => setComparatorEntry((previous) => ({ ...previous, serviceLineId: value }))}
                options={getIDRServiceLines(idrCase).map((line, index) => [
                  line.lineId,
                  `Line ${index + 1}: ${line.cptHcpcs || "code not entered"}`,
                ])}
                emptyLabel="Select a service line..."
              />
              <InputField label="Underlying plan name" value={comparatorEntry.planName} onChange={(value) => setComparatorEntry((previous) => ({ ...previous, planName: value }))} />
              <InputField label="Payer / administrator" value={comparatorEntry.administrator} onChange={(value) => setComparatorEntry((previous) => ({ ...previous, administrator: value }))} />
              <InputField label="Physician / group" value={comparatorEntry.physicianOrGroup} onChange={(value) => setComparatorEntry((previous) => ({ ...previous, physicianOrGroup: value }))} />
              <InputField label="Code / modifiers / units" value={comparatorEntry.codeAndModifiers} onChange={(value) => setComparatorEntry((previous) => ({ ...previous, codeAndModifiers: value }))} />
              <InputField label="Service date(s)" value={comparatorEntry.serviceDates} onChange={(value) => setComparatorEntry((previous) => ({ ...previous, serviceDates: value }))} />
              <InputField label="Location / geography" value={comparatorEntry.location} onChange={(value) => setComparatorEntry((previous) => ({ ...previous, location: value }))} />
              <InputField label="Service scope" value={comparatorEntry.serviceScope} onChange={(value) => setComparatorEntry((previous) => ({ ...previous, serviceScope: value }))} />
              <InputField label="Exact service-line amount" value={comparatorEntry.amount} onChange={(value) => setComparatorEntry((previous) => ({ ...previous, amount: value }))} />
              <InputField label="Currency" value={comparatorEntry.currency} onChange={(value) => setComparatorEntry((previous) => ({ ...previous, currency: value }))} />
              <InputField label="Amount basis" value={comparatorEntry.basis} onChange={(value) => setComparatorEntry((previous) => ({ ...previous, basis: value }))} />
              <InputField label="Payment type and evidence" value={comparatorEntry.paymentBasis} onChange={(value) => setComparatorEntry((previous) => ({ ...previous, paymentBasis: value }))} />
              <SelectField
                label="Human comparability assessment"
                value={comparatorEntry.comparability}
                onChange={(value) => setComparatorEntry((previous) => ({ ...previous, comparability: value }))}
                options={[
                  ["DIRECTLY_COMPARABLE", "Directly comparable"],
                  ["PARTIALLY_COMPARABLE", "Partially comparable"],
                  ["LIMITED", "Limited"],
                  ["PENDING_REVIEW", "Pending review"],
                ]}
              />
              <TextField label="Comparability reasons / limitations" value={comparatorEntry.comparabilityReasons} onChange={(value) => setComparatorEntry((previous) => ({ ...previous, comparabilityReasons: value }))} />
              <SelectField label="Source document" value={comparatorEntry.documentId} onChange={(value) => setComparatorEntry((previous) => ({ ...previous, documentId: value }))} options={manifest.map((item) => [item.id, item.name])} emptyLabel="No source selected" />
              <InputField label="Physical page (optional)" value={comparatorEntry.page} onChange={(value) => setComparatorEntry((previous) => ({ ...previous, page: value }))} type="number" />
            </div>
            <button type="button" className="button" onClick={addComparator}>Add comparator for review</button>
            <div className="idr-record-list">
              {idrCase.comparators.map((comparator) => (
                <article key={comparator.comparatorId}>
                  <strong>{comparator.planName || "Plan unknown"} / {comparator.administrator || "Administrator unknown"}</strong>
                  <p>{comparator.codeAndModifiers || "Code unknown"} · {comparator.serviceScope || "Scope unknown"} · {comparator.amount} {comparator.currency} ({comparator.basis || "basis unknown"})</p>
                  <p>{comparator.serviceDates || "Date unknown"} · {comparator.location || "Geography unknown"} · Human assessment: {comparator.comparability}</p>
                  <p>{comparator.comparabilityReasons || "Comparability limitations not recorded."}</p>
                  <small>Permitted use: PENDING_REVIEW. This entry is not model-verified or approved for the draft.</small>
                  {(() => {
                    const screening = screenComparatorEligibility(comparator, idrCase, manifest);
                    return (
                      <div className="idr-screening-result">
                        <strong>Objective comparison screen: {screening.status}</strong>
                        <p>Permitted use: {screening.permittedUse}. This automated screen only checks recorded fields for exact matches, missing evidence, and differences; legal eligibility is not determined.</p>
                        <ul>{screening.checks.map((check) => <li key={check.code}>{check.detail}</li>)}</ul>
                      </div>
                    );
                  })()}
                </article>
              ))}
              {!idrCase.comparators.length && <p>No comparator payments recorded.</p>}
            </div>
          </section>
        </section>
      )}

      {activeTab === "legal" && (
        <section className="idr-panel">
          <h2>Legal sources</h2>
          <div className="idr-boundary-note">
            Isolated legal research searches official federal sources only. It is separate from
            OCR/model analysis and does not receive patient identity, service dates, claim amounts,
            or document text. Results are research aids, not legal advice or an eligibility
            decision; verify each current authority with counsel.
          </div>
          {legalResearchAnalysis && (
            <section className="idr-subsection" aria-label="Legal research results">
              <h3>Automated research — human review required</h3>
              <p>
                Retrieved {legalResearchAnalysis.processedAt || "date unavailable"}; state jurisdiction{" "}
                {analysisStatus?.legalResearch?.jurisdiction || "not specified"}; as of{" "}
                {analysisStatus?.legalResearch?.asOfDate || "date unavailable"}.
                {Number(legalResearchAnalysis.artifactVersion) !== artifactVersion
                  ? " These results are stale for the current case version."
                  : ""}
              </p>
              {(legalResearchAnalysis.findings || []).map((finding, index) => (
                <article key={`legal-finding-${index}`} className="idr-analysis-result">
                  <strong>{finding.question}</strong>
                  <p>{finding.summary}</p>
                  <small>{finding.status}; verify primary authority and currentness.</small>
                  {finding.citationUrls?.map((citationUrl) => {
                    const source = legalResearchAnalysis.sources?.find((item) => item.url === citationUrl);
                    const safeUrl = safeHttpUrl(citationUrl);
                    return safeUrl ? (
                      <p key={citationUrl}>
                        <a href={safeUrl} target="_blank" rel="noreferrer">
                          {source?.title || citationUrl}
                        </a>
                      </p>
                    ) : null;
                  })}
                </article>
              ))}
              {legalResearchAnalysis.limitations?.length > 0 && (
                <p><strong>Limitations:</strong> {legalResearchAnalysis.limitations.join(" ")}</p>
              )}
            </section>
          )}
          <div className="idr-grid">
            <InputField label="Title" value={legalEntry.title} onChange={(value) => setLegalEntry((previous) => ({ ...previous, title: value }))} />
            <InputField label="Formal citation" value={legalEntry.formalCitation} onChange={(value) => setLegalEntry((previous) => ({ ...previous, formalCitation: value }))} />
            <InputField label="Official URL (if known)" value={legalEntry.url} onChange={(value) => setLegalEntry((previous) => ({ ...previous, url: value }))} type="url" />
            <InputField label="Text / section / page locator" value={legalEntry.locator} onChange={(value) => setLegalEntry((previous) => ({ ...previous, locator: value }))} />
            <TextField label="Proposition to verify" value={legalEntry.proposition} onChange={(value) => setLegalEntry((previous) => ({ ...previous, proposition: value }))} />
            <TextField label="Adverse limitations / status questions" value={legalEntry.limitation} onChange={(value) => setLegalEntry((previous) => ({ ...previous, limitation: value }))} />
          </div>
          <button type="button" className="button" onClick={addLegalSource}>Record unverified source for review</button>
          <div className="idr-record-list">
            {idrCase.legalSources.map((authority) => (
              <article key={authority.authorityId}>
                <strong>{authority.title || authority.formalCitation}</strong>
                <p>{authority.proposition || "No proposition entered."}</p>
                <small>{authority.sourceAccessStatus}; human review: {authority.humanReviewStatus}</small>
                {safeHttpUrl(authority.url) && (
                  <p><a href={safeHttpUrl(authority.url)} target="_blank" rel="noreferrer">{authority.url}</a></p>
                )}
              </article>
            ))}
            {!idrCase.legalSources.length && <p>No legal source has been recorded.</p>}
          </div>
        </section>
      )}

      {activeTab === "draft" && (
        <section className="idr-panel">
          <h2>Editable offer draft</h2>
          {blockers.length > 0 && (
            <ul className="idr-issue-list">
              {blockers.map((issue) => <li key={issue.code}><strong>BLOCKING:</strong> {issue.description}</li>)}
            </ul>
          )}
          <div className="idr-draft-actions">
            {idrCase.outputMode === "combined_idr_submission" && (
              <button
                type="button"
                className="button save"
                onClick={generateDraft}
                disabled={blockers.length > 0 || pricingCodeDiscrepancies.length > 0 || !hasCurrentClinicalAssessment || !hasCurrentPricingAssessment}
              >
                Combine medical necessity and pricing into one document
              </button>
            )}
            <button type="button" className="button save" onClick={generateDraft} disabled={blockers.length > 0 || pricingCodeDiscrepancies.length > 0}>
              Create starting draft
            </button>
            <button
              type="button"
              className="button"
              disabled={!isApprovedForExport || approvalLookupLoading}
              onClick={() => {
                if (!navigator.clipboard?.writeText) {
                  setError("Clipboard access is unavailable in this browser.");
                  return;
                }
                navigator.clipboard.writeText(idrCase.draftMarkdown).then(
                  () => setStatus("Draft copied."),
                  (copyError) => setError(`Could not copy draft: ${copyError.message}`)
                );
              }}
            >
              Copy draft
            </button>
            <button
              type="button"
              className="button"
              disabled={!isApprovedForExport || approvalLookupLoading}
              onClick={() => downloadMarkdown(idrCase.draftMarkdown, `${caseId || "idr-offer-draft"}.md`)}
            >
              Download Markdown
            </button>
          </div>
          {pricingCodeDiscrepancies.length > 0 && (
            <div className="idr-alert idr-alert-warning">
              Resolve the displayed code-to-remittance discrepancies and rerun pricing analysis before drafting.
            </div>
          )}
          {idrCase.draftReviewNotes?.length > 0 && (
            <section className="idr-subsection" aria-label="Internal review notes">
              <h3>Internal review notes — not part of the draft</h3>
              <ul>{idrCase.draftReviewNotes.map((note, index) => <li key={`draft-review-note-${index}`}>{note}</li>)}</ul>
            </section>
          )}
          {isDraftStale && <div className="idr-alert idr-alert-warning">Case or source inputs changed after this draft was created. Regenerate and review the draft.</div>}
          <textarea
            className="idr-draft-editor"
            aria-label="Editable offer draft"
            value={idrCase.draftMarkdown || ""}
            onChange={(event) => setIdrCase((previous) => ({ ...previous, draftMarkdown: event.target.value }))}
            placeholder="Combine the medical and pricing analyses after uploading sources and describing your offer."
            rows={24}
          />
          <p className="idr-muted">
            Copy and download are enabled only after a saved version is explicitly approved for
            export. Editing the draft or its source inputs invalidates this gate. Export approval is
            not filing, submission, or authorization to transmit to an IDR entity.
          </p>
        </section>
      )}

      {activeTab === "review" && (
        <section className="idr-panel">
          <h2>Evidence & review report</h2>
          <p><strong>Status:</strong> {reviewReport.status} · <strong>Legal research:</strong> {reviewReport.researchStatus} · <strong>Auto-filing:</strong> No</p>
          <h3>Issues and review actions</h3>
          <ul className="idr-issue-list">
            {reviewReport.issues.map((issue, index) => (
              <li key={`${issue.code}-${index}`} className={`idr-severity-${issue.severity.toLowerCase()}`}>
                <strong>{issue.severity} · {issue.code}:</strong> {issue.description}
              </li>
            ))}
            {!reviewReport.issues.length && <li>No review issues detected by the local checks. This does not establish factual accuracy.</li>}
          </ul>
          <h3>Documents</h3>
          <ul className="idr-issue-list">
            {manifest.map((document) => (
              <li key={document.id}>
                <strong>{document.name}</strong> — {document.role}; page count {document.pageCount || "unknown"}; {document.storageKey ? "private upload present" : "not uploaded"}; {document.extractionStatus}; hash {document.hash ? `${document.hash.slice(0, 16)}…` : "pending"}.
                {document.pageAssignments?.length > 0 && (
                  <span> Page assignments: {document.pageAssignments.map((assignment) => `${assignment.pages.join(",")}=${assignment.role}`).join("; ")}.</span>
                )}
              </li>
            ))}
            {!manifest.length && <li>No evidence documents are attached.</li>}
          </ul>
          <h3>Manual source reference checks</h3>
          <ul className="idr-issue-list">
            {(reviewReport.sourceReferences || []).map((item, index) => (
              <li key={`source-${index}`}>
                {item.validation.valid
                  ? `STRUCTURALLY VALID; ${item.validation.attached ? "ATTACHED" : "NOT YET ATTACHED"}; NOT SUBSTANTIVELY VERIFIED`
                  : `INVALID: ${item.validation.reason}`}
              </li>
            ))}
            {!reviewReport.sourceReferences.length && <li>No source-linked clinical work events recorded.</li>}
          </ul>
          <h3>Review history</h3>
          <ul className="idr-issue-list">
            {(idrCase.reviewHistory || []).map((entry, index) => (
              <li key={`${entry.timestamp}-${index}`}>{entry.author} · {entry.timestamp} · {entry.source} · {entry.changedFields.join(", ")}</li>
            ))}
            {!idrCase.reviewHistory?.length && <li>No edits recorded after the initial case creation.</li>}
          </ul>
          <section className="idr-subsection">
            <DocumentExportApprovalPanel
              approved={isApprovedForExport}
              approvedBy={caseApproval?.approvedBy}
              approvedAt={caseApproval?.approvedAt}
              checked={approvalConfirmed}
              onCheckedChange={setApprovalConfirmed}
              onApprove={approveDraftForExport}
              loading={approvalLoading}
              disabled={
                !caseId ||
                !artifactVersion ||
                approvalLookupLoading ||
                saving ||
                blockers.length > 0 ||
                pricingCodeDiscrepancies.length > 0 ||
                !idrCase.draftMarkdown ||
                isDraftStale ||
                !manifest.length ||
                manifest.some((document) => !document.storageKey)
              }
              checkboxText="I reviewed the current draft and its source documents, including limitations and unresolved issues, and approve this saved version for local export."
            />
            {isDraftStale && (
              <p className="idr-alert idr-alert-warning">
                The draft inputs changed. Regenerate, save a new case version, and review again
                before approval.
              </p>
            )}
          </section>
          <p className="idr-muted">Saving creates a new artifact version and invalidates prior approval. OCR/model results are version-bound and require human verification. Saved IDR case activity resets the configured seven-year retention period.</p>
        </section>
      )}

      <footer className="idr-workflow-footer">
        <span>{blockers.length ? `${blockers.length} blocking field(s) before draft synthesis` : "Core draft fields present; verify all evidence and citations"}</span>
        <button type="button" className="button save" onClick={saveCase} disabled={saving}>
          {saving ? "Saving..." : "Save case"}
        </button>
      </footer>
    </main>
  );
}
